/**
 * HOOK: useStockValidation
 * 
 * Hook personalizado para manejar la validación de stock en cotizaciones.
 * Proporciona funciones simplificadas para:
 * - Verificar stock al crear/editar/aceptar cotizaciones
 * - Mostrar mensajes de estado al usuario
 * - Gestionar errores de forma uniforme
 * 
 * ⚠️ IMPORTANTE: Todo está protegido por feature flags.
 *    Si el flag está desactivado, las funciones simulan éxito sin hacer nada.
 */

import { useState, useCallback } from 'react';
import stockVerificacionService from '../services/stockVerificacionService';
import { isFeatureEnabled, FLUJO_STOCK_CONFIG } from '../config/flujoStock.config';


export const useStockValidation = () => {
  const [validando, setValidando] = useState(false);
  const [error, setError] = useState(null);
  const [ultimoResultado, setUltimoResultado] = useState(null);

  /**
   * Verificar stock para una cotización (sin procesar acciones)
   * Útil para mostrar advertencias antes de aceptar
   */
  const verificarStock = useCallback(async (cotizacionId, articulos) => {
    if (!isFeatureEnabled('VALIDACION_STOCK_HABILITADA')) {
      return { stockSuficiente: true, articulosFaltantes: [] };
    }

    setValidando(true);
    setError(null);

    try {
      const resultado = await stockVerificacionService.verificarStockCotizacion(
        cotizacionId,
        articulos
      );
      
      setUltimoResultado(resultado);
      return resultado;
      
    } catch (err) {
      setError(err.message || 'Error verificando stock');
      return { stockSuficiente: true, articulosFaltantes: [] }; // Fallback seguro
    } finally {
      setValidando(false);
    }
  }, []);


  /**
   * Procesar cotización completa con validación de stock
   * Esta es la función principal para usar al crear/editar/aceptar
   */
  const procesarConStock = useCallback(async ({
    cotizacionId,
    cotizacionNumero,
    clienteNombre,
    empresaNombre,
    articulos,
    accion,
    onSuccess,
    onError,
    onStockInsuficiente
  }) => {
    // Si está desactivado, simular éxito y ejecutar callback
    if (!isFeatureEnabled('VALIDACION_STOCK_HABILITADA')) {
      onSuccess?.({ stockSuficiente: true, flujoNuevo: false });
      return { exito: true, stockSuficiente: true };
    }

    setValidando(true);
    setError(null);

    try {
      const resultado = await stockVerificacionService.procesarCotizacionConStock({
        cotizacionId,
        cotizacionNumero,
        clienteNombre,
        empresaNombre,
        articulos,
        accion
      });

      if (resultado.exito) {
        setUltimoResultado(resultado);
        
        if (resultado.stockSuficiente) {
          // Stock OK - continuar flujo normal
          onSuccess?.({ 
            stockSuficiente: true, 
            flujoNuevo: true,
            mensaje: resultado.mensaje 
          });
        } else {
          // Stock insuficiente - enviado a compras
          onStockInsuficiente?.({
            articulosFaltantes: resultado.articulosFaltantes,
            mensaje: resultado.mensaje
          });
          
          // También llamar onSuccess porque la operación fue exitosa
          onSuccess?.({ 
            stockSuficiente: false, 
            flujoNuevo: true,
            articulosFaltantes: resultado.articulosFaltantes,
            mensaje: resultado.mensaje
          });
        }
        
        return resultado;
      } else {
        throw new Error(resultado.error || 'Error procesando cotización');
      }
      
    } catch (err) {
      setError(err.message);
      onError?.(err.message);
      return { exito: false, error: err.message };
    } finally {
      setValidando(false);
    }
  }, []);


  /**
   * Verificar stock de forma rápida (para mostrar alertas inline)
   * Retorna true/false inmediatamente sin mostrar loading
   */
  const tieneStockSuficiente = useCallback(async (articulos) => {
    if (!isFeatureEnabled('VALIDACION_STOCK_HABILITADA')) {
      return true;
    }

    try {
      const resultado = await stockVerificacionService.verificarStockCotizacion(
        null, // No necesitamos cotizacionId para verificación simple
        articulos
      );
      return resultado.stockSuficiente;
    } catch {
      return true; // Si falla, asumir que hay stock (fallback seguro)
    }
  }, []);


  /**
   * Mostrar toast/notificación al usuario según el resultado
   */
  const mostrarNotificacionResultado = useCallback((resultado) => {
    if (!resultado || !isFeatureEnabled('VALIDACION_STOCK_HABILITADA')) {
      return;
    }

    const toast = document.createElement('div');
    
    if (resultado.stockSuficiente) {
      // Stock OK
      toast.className = 'fixed bottom-4 right-4 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
      toast.innerHTML = `✅ ${resultado.mensaje || FLUJO_STOCK_CONFIG.MENSAJES.STOCK_SUFICIENTE}`;
    } else {
      // Stock insuficiente
      toast.className = 'fixed bottom-4 right-4 bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
      toast.innerHTML = `⚠️ ${resultado.mensaje || FLUJO_STOCK_CONFIG.MENSAJES.STOCK_INSUFICIENTE}`;
    }
    
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
  }, []);


  /**
   * Limpiar estado del hook
   */
  const reset = useCallback(() => {
    setValidando(false);
    setError(null);
    setUltimoResultado(null);
  }, []);


  return {
    // Estado
    validando,
    error,
    ultimoResultado,
    flujoHabilitado: isFeatureEnabled('VALIDACION_STOCK_HABILITADA'),
    
    // Funciones
    verificarStock,
    procesarConStock,
    tieneStockSuficiente,
    mostrarNotificacionResultado,
    reset,
    
    // Helpers
    mensajes: FLUJO_STOCK_CONFIG.MENSAJES
  };
};


export default useStockValidation;
